'use client';

import { signOutAction } from "@/app/[locale]/actions/auth";
import { Button } from "./ui/button";
import { NavigationButton } from "./navigation-button";
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "./ui/popover";
import { ThemeSwitcher } from "./theme-switcher";
import { useState, useTransition } from "react";
import { useTranslations } from "next-intl";
import { Loader2, LogOut, Menu, History } from "lucide-react";
import { AccountCreditsPanel } from "./usage-indicator";
import Link from "next/link";
import { CharacterPricingDialog } from "./translate/character-pricing-dialog";
import { Skeleton } from "./ui/skeleton";

interface AuthButtonProps {
  user: {
    email?: string;
    is_anonymous?: boolean;
  } | null;
  friendlyName?: string | null;
  onHistoryClick?: () => void;
  canManagePassword?: boolean;
  isLoading?: boolean;
}

export default function HeaderAuthClient({ user, friendlyName, onHistoryClick, canManagePassword, isLoading = false }: AuthButtonProps) {
  const t = useTranslations('auth');
  const [isPending, startTransition] = useTransition();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showPricingDialog, setShowPricingDialog] = useState(false);

  const handleSignOut = () => {
    startTransition(async () => {
      await signOutAction();
    });
  };

  // Use friendly name if available, otherwise fall back to email
  const displayName = friendlyName || user?.email || '';

  if (isLoading) {
    return <Skeleton className="h-9 w-36" />;
  }

  return user && !user.is_anonymous ? (
    <div className="flex items-center">
      <Popover open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-9 gap-2 px-3"
          >
            <span className="text-sm font-medium">{displayName}</span>
            <Menu className="h-4 w-4" />
            <span className="sr-only">{t('openSettings')}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="end">
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-medium leading-none">{t('account')}</p>
              <p className="text-sm text-muted-foreground">{user.email}</p>
            </div>

            {onHistoryClick && (
              <PopoverClose asChild>
                <Button
                  variant="ghost"
                  onClick={onHistoryClick}
                  className="w-full justify-start h-9 px-2"
                >
                  <History className="mr-2 h-4 w-4" />
                  {t('history')}
                </Button>
              </PopoverClose>
            )}

            {/* Account credits panel - only show for registered users */}
            <div className="border-t border-b py-3">
              <AccountCreditsPanel
                onGetMoreCharactersClick={() => {
                  setIsMenuOpen(false);
                  setShowPricingDialog(true);
                }}
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">{t('theme')}</span>
                <ThemeSwitcher />
              </div>



              <div className="border-t pt-3 space-y-2">
                <PopoverClose asChild>
                  <Link href="/terms" className="block text-sm text-muted-foreground hover:text-foreground">
                    {t('termsOfService')}
                  </Link>
                </PopoverClose>
                <PopoverClose asChild>
                  <Link href="/privacy" className="block text-sm text-muted-foreground hover:text-foreground">
                    {t('privacyPolicy')}
                  </Link>
                </PopoverClose>
                {canManagePassword && (
                  <PopoverClose asChild>
                    <Link href="/reset-password" className="block text-sm text-muted-foreground hover:text-foreground">
                      {t('changePassword')}
                    </Link>
                  </PopoverClose>
                )}
              </div>

              <Button
                onClick={handleSignOut}
                variant="ghost"
                disabled={isPending}
                className="w-full justify-start h-9 px-2"
              >
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('signingOut')}
                  </>
                ) : (
                  <>
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('signOut')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      <CharacterPricingDialog open={showPricingDialog} onOpenChange={setShowPricingDialog} />
    </div>
  ) : (
    <div className="flex gap-2">
      <NavigationButton href="/sign-in" size="sm" variant={"default"}>
        {t('signIn.title')}
      </NavigationButton>
      <NavigationButton
        href="/sign-up"
        size="sm"
        variant={"outline"}
        className="hidden md:inline-flex"
      >
        {t('signUp.title')}
      </NavigationButton>
    </div>
  );
}
